import * as React from 'react';

import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type DateRange } from 'react-day-picker';
import { defineMessages, useIntl } from 'react-intl';
import { ZodError } from 'zod';

import { Button } from 'src/registry/ui/Button';
import { Calendar } from 'src/registry/ui/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'src/registry/ui/Popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { cn } from 'src/utils/shadcn';

interface DatePickerWithRangeProps extends Omit<React.HTMLAttributes<HTMLInputElement>, 'onSelect' | 'onChange'> {
    buttonClassName?: string;
    error?: ZodError;
    value?: DateRange;
    onChange: (date: DateRange) => void;
}

type RangeOptions = 'CUSTOM' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR' | 'INDEFINITE';

type RangeOption = {
    value: RangeOptions;
    label: {
        id: string;
        defaultMessage: string;
    };
};

const rangeOptions: Record<string, RangeOption> = {
    WEEK: { value: 'WEEK', label: { id: 'one-week', defaultMessage: '1 Week' } },
    MONTH: { value: 'MONTH', label: { id: 'one-month', defaultMessage: '1 Month' } },
    QUARTER: { value: 'QUARTER', label: { id: 'three-months', defaultMessage: '3 Months' } },
    YEAR: { value: 'YEAR', label: { id: 'one-year', defaultMessage: '1 Year' } },
    INDEFINITE: { value: 'INDEFINITE', label: { id: 'indefinite', defaultMessage: 'Indefinite' } },
    CUSTOM: { value: 'CUSTOM', label: { id: 'custom', defaultMessage: 'Custom' } },
};

const messages = defineMessages({
    indefinite: {
        id: 'indefinite',
        defaultMessage: 'Indefinite',
    },
    pickDateRange: {
        id: 'pick-date-range',
        defaultMessage: 'Pick a date range',
    },
    membershipDurationPlaceholder: {
        id: 'membership-duration-placeholder',
        defaultMessage: 'Select duration...',
    },
});

const DatePickerWithRange = ({ className, buttonClassName, error, onChange, value }: DatePickerWithRangeProps) => {
    const [range, setRange] = React.useState<RangeOptions>();
    const { formatMessage, formatDate } = useIntl();

    const calculateEndDate = (range: RangeOptions, startDate: Date) => {
        const endDate = new Date(startDate);
        if (range == rangeOptions.WEEK.value) {
            endDate.setDate(startDate.getDate() + 7);
        } else if (range == rangeOptions.MONTH.value) {
            endDate.setMonth(startDate.getMonth() + 1);
            const fromDay = startDate.getDate();
            const toDay = endDate.getDate();
            // Overflow edge case
            if (fromDay > 28 && toDay < 4) {
                endDate.setDate(endDate.getDate() - toDay);
            }
        } else if (range == rangeOptions.QUARTER.value) {
            endDate.setMonth(startDate.getMonth() + 3);
            const fromDay = startDate.getDate();
            const toDay = endDate.getDate();
            // Overflow edge case
            if (fromDay > 28 && toDay < 4) {
                endDate.setDate(endDate.getDate() - toDay);
            }
        } else if (range == rangeOptions.YEAR.value) {
            endDate.setFullYear(startDate.getFullYear() + 1);
        } else if (range == rangeOptions.INDEFINITE.value) {
            endDate.setFullYear(startDate.getFullYear() + 100);
        }

        return endDate;
    };

    const onSelect = (selectedRange: DateRange, selectedDay: Date) => {
        if (range && selectedRange?.from && range !== rangeOptions.CUSTOM.value) {
            selectedRange.from = selectedDay;
            selectedRange.to = calculateEndDate(range, selectedDay);
        }
        onChange(selectedRange);
    };

    // Determine the range days based on the initial dates (when editing a subscription)
    React.useEffect(() => {
        if (!value || !value.from || !value.to) {
            return;
        }

        const endDateTime = value.to.getTime();
        // A hacky way but we reverse engineer the to date by calculating all the possible options to determine the range
        if (calculateEndDate(rangeOptions.WEEK.value, value.from).getTime() === endDateTime) {
            setRange(rangeOptions.WEEK.value);
        } else if (calculateEndDate(rangeOptions.MONTH.value, value.from).getTime() === endDateTime) {
            setRange(rangeOptions.MONTH.value);
        } else if (calculateEndDate(rangeOptions.QUARTER.value, value.from).getTime() === endDateTime) {
            setRange(rangeOptions.QUARTER.value);
        } else if (calculateEndDate(rangeOptions.YEAR.value, value.from).getTime() === endDateTime) {
            setRange(rangeOptions.YEAR.value);
        } else if (calculateEndDate(rangeOptions.INDEFINITE.value, value.from).getTime() <= endDateTime) {
            setRange(rangeOptions.INDEFINITE.value);
        } else {
            setRange(rangeOptions.CUSTOM.value);
        }
    }, []);

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    id="date"
                    variant="outline"
                    className={cn(
                        'w-full justify-start text-left text-base font-normal',
                        error &&
                            'border-red-300 hover:border-red-500 focus-visible:border-red-500 focus-visible:ring-red-200',
                        buttonClassName,
                    )}
                >
                    <FontAwesomeIcon icon={faCalendar} className="text-primary-700 mr-1 size-4" />
                    {value?.from ? (
                        value.to ? (
                            `${formatDate(value.from)} - ${Math.round((value.to.getTime() - value.from.getTime()) / (1000 * 60 * 60 * 24)) >= 3650 ? formatMessage(messages.indefinite) : formatDate(value.to)}`
                        ) : (
                            formatDate(value.from)
                        )
                    ) : (
                        <span className="text-gray-400">{formatMessage(messages.pickDateRange)}</span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className={cn('w-auto min-w-96 p-4', className)} align="start">
                <Select defaultValue={range} onValueChange={(value) => setRange(value as RangeOptions)}>
                    <SelectTrigger>
                        <SelectValue placeholder={formatMessage(messages.membershipDurationPlaceholder)} />
                    </SelectTrigger>
                    <SelectContent>
                        {Object.values(rangeOptions).map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                                {formatMessage(option.label)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                {range && (
                    <Calendar
                        mode="range"
                        required
                        defaultMonth={value?.from}
                        selected={value}
                        className={cn('-m-1 mt-4 p-0', range ?? 'pointer-events-none opacity-20')}
                        onSelect={onSelect}
                        numberOfMonths={2}
                        timeZone="UTC"
                    />
                )}
            </PopoverContent>
        </Popover>
    );
};

export { DatePickerWithRange };
